import ng from 'angular';
import { Component, Input } from 'angular-ts-decorators';

import { fetchCustomerInfo } from '@gn-audio/core/api/pricing/fetch-customer-info';
import { ICustomer, IFromPrice, IPrice, ISFCCGroupPrice, ISFCCProductPrice } from '@gn-audio/core/api/pricing/models';
import { IProductService } from '@gn-audio/sfcc-basket/models';
import { getAvailabilityState } from '@gn-audio/core/api/pricing/utilities';
import { IProductWithAttributes } from '@gn-audio/core/api/product-catalogue/models/product-with-attributes';
import { fetchGroupRetailers } from '@gn-audio/core/api/retailers/fetch-group-retailers';
import { IGroupRetailer } from '@gn-audio/core/api/retailers/models';
import { AvailabilityState, SegmentType } from '@gn-audio/core/common/enums';

import { TranslationKey } from 'src/Foundation/Translation/platform/app/translation-key.enum';
import { CustomizerEvents, ScrollTargetSelector } from '../models';
import { getComingSoonDictionaryKey, notifySubscribers, scrollToTarget } from '../utils';

import template from './customizer-buttons.component.html';

@Component({
  selector: 'customizerButtons',
  template,
})
export class CustomizerButtonsComponent implements ng.IController {
  public static $inject: ReadonlyArray<string> = ['$q'];

  @Input() public readonly group?: IProductWithAttributes;
  @Input() public readonly isPriceOnActionButton?: boolean;
  @Input() public readonly isRetailerLogicEnabled?: boolean
  @Input() public readonly isEppLogicEnabled?: boolean;
  @Input() public readonly engravingMessage?: string;
  @Input() public readonly services?: IProductService[];
  @Input() public readonly selectedServicesPrices?: any;
  @Input() public readonly accessoriesPrices?: (IPrice | ISFCCProductPrice)[];

  public groupState?: AvailabilityState;
  public availableRetailers: IGroupRetailer[] = [];
  public isUserEPPOne: boolean;

  constructor(
    private readonly $q: ng.IQService,
  ) {}

  public $onInit(): void {
    if (!this.isEppLogicEnabled) {
      return;
    }

    this.$q
      .when(fetchCustomerInfo())
      .then((customer: ICustomer | null) => (this.isUserEPPOne = !!customer?.c_isEPPCustomer))
      .catch(() => (this.isUserEPPOne = false));
  }

  public $onChanges(): void {
    if (!this.isRetailerLogicEnabled || !this.group) {
      return;
    }

    this.$q
    .when(fetchGroupRetailers(this.group.groupId))
    .then((retailers: IGroupRetailer[] | null) => this.availableRetailers = retailers || []);
  }

  public onPriceLoaded(price?: IFromPrice | ISFCCGroupPrice | null) {
    notifySubscribers(CustomizerEvents.SelectionPriceLoaded, price);
    this.groupState = getAvailabilityState(price);
  }

  public onButtonClick(): void {
    scrollToTarget(document.querySelector(ScrollTargetSelector.Form));
  }

  public get dictionaryKey(): TranslationKey | undefined {
    return this.isComingSoonState ? getComingSoonDictionaryKey(this.segmentType) : undefined;
  }

  public get isComingSoonState(): boolean {
    return this.groupState === AvailabilityState.ComingSoon;
  }

  public get canProductBeBought(): boolean {
    return this.groupState < AvailabilityState.WhereToBuy;
  }

  public get displayRetailersSection(): boolean {
    return !!this.isRetailerLogicEnabled && !!this.availableRetailers.length
      && this.groupState === AvailabilityState.WhereToBuy;
  }

  public get isSoldOutButtonDisplayed(): boolean {
    return this.isUserEPPOne && !this.canProductBeBought;
  }

  public get canActionButtonBeShown(): boolean {
    return !this.isComingSoonState && !this.displayRetailersSection
      && (!this.isUserEPPOne || (this.isUserEPPOne && this.canProductBeBought));
  }

  public get hasGroupOnlyExclusiveRetailer(): boolean {
    return this.availableRetailers.length === 1 && !!this.availableRetailers[0].exclusive;
  }

  private get segmentType(): SegmentType | undefined {
    return this.group?.segmentType;
  }

  public get servicesPrices(): (IPrice | ISFCCProductPrice)[] {
    return this.selectedServicesPrices ?? null;
  }
}
